import { publicHref } from '../lib/publicUrls';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { emitSemanticEvent } from '../lib/semanticEvents';

const SEEN_KEY = 'dv_tour_invitation_seen';

function hasSeenInvitation() {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function rememberInvitation() {
  try {
    window.sessionStorage.setItem(SEEN_KEY, 'true');
  } catch {
    // The invitation still dismisses when storage is disabled.
  }
}

const isEditingForm = () => Boolean(document.activeElement?.closest('input, textarea, select, [contenteditable="true"]'));

// Small invitation shown at most once per browser-tab session, after the visitor
// scrolls past the element in `triggerRef`. It never moves keyboard focus, and the
// stylesheet hides it while the site menu is open.
export default function TourInvitation({ triggerRef }) {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const panelRef = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger || hasSeenInvitation() || !('IntersectionObserver' in window)) return undefined;

    // The opening counts as passed once it has scrolled under the fixed header.
    const headerHeight = document.querySelector('.dv-site-header')?.offsetHeight || 0;
    const observer = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= headerHeight;
      if (!passed || isEditingForm()) return;
      rememberInvitation();
      setVisible(true);
      observer.disconnect();
    }, { rootMargin: `-${headerHeight}px 0px 0px 0px` });
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [triggerRef]);

  // The page's own tour request (TourClosing) makes the invitation redundant: step aside once it shows.
  useEffect(() => {
    const tourRequest = document.getElementById('plan-your-visit');
    if (!visible || !tourRequest || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(false); });
    observer.observe(tourRequest);
    return () => observer.disconnect();
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    const focusInside = panelRef.current?.contains(document.activeElement);
    setVisible(false);
    if (!focusInside) return;
    // Keyboard users who tabbed into the panel continue from where they were.
    const target = returnFocus.current?.isConnected ? returnFocus.current : document.getElementById('main-content');
    target?.focus({ preventScroll: true });
  };

  return (
    <aside
      ref={panelRef}
      className="dv-tour-invite"
      aria-labelledby="tour-invite-title"
      onFocus={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) returnFocus.current = event.relatedTarget;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') dismiss();
      }}
    >
      <button type="button" className="dv-tour-invite__close" aria-label="Dismiss tour invitation" onClick={dismiss}>
        <X aria-hidden="true" />
      </button>
      <h2 className="dv-size-subheading" id="tour-invite-title">Find your community.</h2>
      <div className="dv-tour-invite__actions">
        <Link
          className="dv-button dv-button--light"
          to={publicHref("/book-a-tour")}
          onClick={() => emitSemanticEvent('book_tour_cta_click', {
            page_path: location.pathname,
            cta_placement: 'tour_invitation',
            component_context: 'homepage_tour_invitation',
            destination: '/book-a-tour',
            location_context: 'unspecified',
          })}
        >
          Book a Tour
        </Link>
        <Link className="dv-text-link" to={publicHref("/membership")}>View Memberships</Link>
      </div>
    </aside>
  );
}
